import { Injectable } from '@angular/core';
import { EmitterService, ListHelperService, PopUpService, ProfileService, StoreService } from 'core/services';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { MarsQuestionsService } from './mars-questions.service';

@Injectable()
export class MarsQuestionsListService {
  userRole: any;
  dataSource: TableDatasource;

  constructor(
    public translate: TranslateService,
    public profileService: ProfileService,
    public listHelper: ListHelperService,
    public popupService: PopUpService,
    public marsQuestionsService: MarsQuestionsService
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  async getDatasource(listId, options: any = {}) {
    const dataSource = new TableDatasource({
      sort: '-created_at',
      columns: await this.getColumns(),
      load: (props) => this.fetch(props, dataSource),
      ...options
    });
    await dataSource.refreshData();
    StoreService.create(listId, dataSource);
    return dataSource;
  }

  fetch({ page, limit, sort, search, filters }, dataSource: TableDatasource) {
    // const { clientId, agentIds, distributorId, state, ...$filterValues } = filters;
    // const $filter = this.listHelper.buildFilterString($filterValues, dataSource);
    const paginationOptions = {
      page,
      limit,
      sort,
      search,
      ...filters
    };
    return this.marsQuestionsService.fetch(paginationOptions).then((response) => response);
  }

  async getColumns() {
    const columns = [
      {
        source: 'question',
        label: this.translate.instant('customizations.documents.questions.text'),
        dataType: DataTypes.string,
        sortable: true
      },
      {
        label: this.translate.instant('customizations.documents.questions.numAnswers'),
        dataType: DataTypes.number,
        sortable: false,
        valueParser: (value, question) => question.right + question.wrong
      },
      {
        source: 'choices',
        label: this.translate.instant('customizations.documents.questions.correctAnswer'),
        dataType: DataTypes.array,
        style: { width: '140px' },
        sortable: false,
        valueParser: (value) =>
          value.sort((x, y) => (x.isCorrect === y.isCorrect ? 0 : x.isCorrect ? -1 : 1)).map((choice) => choice.text),
        cellRenderer: (renderer, value) => {
          const rightChoice = ['#acf1ed'];
          const wrongChoice = ['#e1e4e8'];
          const [firstItem, ...restItems] = value;
          return renderer`
            <div class="tags">
              <gl-tag .colors="${rightChoice}" label="${firstItem}"></gl-tag>
              ${
                restItems && restItems.length > 0
                  ? renderer`
                      <gl-tooltip posRight light>
                        <gl-tag slot="trigger" .colors="${wrongChoice}" .label="+${restItems.length}"></gl-tag>
                        ${restItems.map(
                          (item, index) => renderer`<p><gl-tag .colors="${wrongChoice}" label="${item}"></gl-tag></p>`
                        )}
                      </gl-tooltip>
                    `
                  : ''
              }
            </div>
          `;
        }
      },
      {
        source: 'right',
        label: this.translate.instant('customizations.documents.questions.correct'),
        dataType: DataTypes.number,
        sortable: true
      },
      {
        source: 'wrong',
        label: this.translate.instant('customizations.documents.questions.incorrect'),
        dataType: DataTypes.number,
        sortable: true
      },
      {
        source: 'wildcard',
        label: this.translate.instant('customizations.documents.questions.wildcard'),
        dataType: DataTypes.number,
        sortable: true
      },
      {
        source: 'createdByEmail',
        label: this.translate.instant('customizations.documents.questions.author'),
        dataType: DataTypes.string,
        sortable: true,
        valueParser: (value) => value || '--'
      },
      {
        source: 'status',
        label: this.translate.instant('customizations.documents.questions.status'),
        dataType: DataTypes.string,
        sortable: true,
        valueParser: (value) => this.translate.instant(`questions.status.${(value || 'default').toLowerCase()}`)
      },
      {
        source: 'likes',
        label: this.translate.instant('customizations.documents.questions.likes'),
        dataType: DataTypes.number,
        sortable: true
      },
      {
        source: 'dislikes',
        label: this.translate.instant('customizations.documents.questions.dislikes'),
        dataType: DataTypes.number,
        sortable: true
      },
      {
        source: 'created_at',
        label: this.translate.instant('customizations.documents.questions.creationDate'),
        dataType: DataTypes.datetime,
        sortable: true
      },
      {
        alignment: 'right',
        showInColumnChooser: false,
        visible: this.profileService.canExecute('mars.tutorization.questions'),
        style: { width: '75px' },
        cellRenderer: (renderer, value, question) => {
          return renderer`
            <gl-context-menu medium>
              <gl-button slot="target" id="${question.id}" icon icon-center="glicon-dots"></gl-button>

              <gl-menu-item 
                text="${this.translate.instant('customizations.documents.questions.certify')}" 
                @click="${() => this.listHelper.run(() => this.certifyQuestion(question))}">
              </gl-menu-item>

              <gl-menu-item 
                text="${this.translate.instant('customizations.documents.questions.publish')}" 
                @click="${() => this.listHelper.run(() => this.publishQuestion(question))}">
              </gl-menu-item>

              <gl-menu-item 
                text="${this.translate.instant('customizations.documents.questions.cancel')}" 
                @click="${() => this.listHelper.run(() => this.cancelQuestion(question))}">
              </gl-menu-item>

              <gl-menu-item 
                text="${this.translate.instant('customizations.documents.questions.delete')}" 
                @click="${() => this.listHelper.run(() => this.deleteQuestion(question.id))}">
              </gl-menu-item>

              <gl-menu-item 
                text="${this.translate.instant('actions.edit')}" 
                @click="${() => this.listHelper.run(() => this.editQuestion(question))}">
              </gl-menu-item>

            </gl-context-menu>`;
        }
      }
    ];
    return columns;
  }

  editQuestion(question) {
    EmitterService.get('editMarsQuestion').emit(question);
  }

  deleteQuestion(questionId) {
    return this.popupService
      .confirmDelete({
        title: this.translate.instant('customizations.documents.questions.confirmDelete'),
        text: this.translate.instant('customizations.documents.questions.deleteMessage')
      })
      .then(() => this.marsQuestionsService.delete(questionId))
      .then(() => EmitterService.get('refreshQuestionsList').emit(true))
      .catch((err) => {
        if (err !== 'cancel' && err !== 'esc') {
          this.popupService.showError({
            title: this.translate.instant('customizations.documents.questions.deleteError'),
            text: err.serverMessage
          });
        }
      });
  }

  cancelQuestion(question) {
    this.popupService
      .confirm({
        title: this.translate.instant('customizations.documents.questions.confirmCancel'),
        text: this.translate.instant('customizations.documents.questions.cancelMessage')
      })
      .then(() => this.marsQuestionsService.cancelQuestion(question))
      .then(() => EmitterService.get('refreshQuestionsList').emit(true))
      .catch((err) => {
        if (err !== 'cancel' && err !== 'esc') {
          this.popupService.showError({
            title: this.translate.instant('customizations.documents.questions.cancelError'),
            text: err.serverMessage
          });
        }
      });
  }

  publishQuestion(question) {
    this.popupService
      .confirm({
        title: this.translate.instant('customizations.documents.questions.confirmPublish'),
        text: this.translate.instant('customizations.documents.questions.publishMessage')
      })
      .then(() => this.marsQuestionsService.publishQuestion(question))
      .then(() => EmitterService.get('refreshQuestionsList').emit(true))
      .catch((err) => {
        if (err !== 'cancel' && err !== 'esc') {
          this.popupService.showError({
            title: this.translate.instant('customizations.documents.questions.publishError'),
            text: err.serverMessage
          });
        }
      });
  }

  certifyQuestion(question) {
    this.popupService
      .confirm({
        title: this.translate.instant('customizations.documents.questions.confirmCertify'),
        text: this.translate.instant('customizations.documents.questions.certifyMessage')
      })
      .then(() => this.marsQuestionsService.certifyQuestion(question))
      .then(() => EmitterService.get('refreshQuestionsList').emit(true))
      .catch((err) => {
        if (err !== 'cancel' && err !== 'esc') {
          this.popupService.showError({
            title: this.translate.instant('customizations.documents.questions.certifyError'),
            text: err.serverMessage
          });
        }
      });
  }
}
